import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useRef, useMemo } from 'react';
const ZOOM_CONFIG = {
    day: { dayWidth: 30, label: 'Day' },
    week: { dayWidth: 10, label: 'Week' },
    month: { dayWidth: 3, label: 'Month' },
};
const ROW_HEIGHT = 36;
const BAR_CONFIG = {
    'Epic': { color: '#6366f1', height: 24 },
    'Story': { color: '#3b82f6', height: 18 },
    'Task': { color: '#3b82f6', height: 18 },
    'Bug': { color: '#ef4444', height: 18 },
    'Sub-task': { color: '#93c5fd', height: 14 },
};
function daysBetween(d1, d2) {
    return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}
function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}
function formatMonth(date) {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
function formatWeek(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
export function PlanGanttView({ epics, unparentedIssues, expandedKeys, onToggleExpand }) {
    const [zoom, setZoom] = useState('week');
    const leftPaneRef = useRef(null);
    const rightPaneRef = useRef(null);
    const [leftPaneWidth, setLeftPaneWidth] = useState(300);
    const resizingRef = useRef(false);
    const { dayWidth } = ZOOM_CONFIG[zoom];
    // Flatten visible rows
    const visibleRows = useMemo(() => {
        const rows = [];
        const addRows = (issues, depth) => {
            for (const issue of issues) {
                rows.push({ issue, depth });
                if (expandedKeys.has(issue.issueKey) && issue.children.length > 0) {
                    addRows(issue.children, depth + 1);
                }
            }
        };
        addRows(epics, 0);
        if (unparentedIssues.length > 0) {
            addRows(unparentedIssues, 0);
        }
        return rows;
    }, [epics, unparentedIssues, expandedKeys]);
    // Calculate date range
    const { startDate, endDate, totalDays } = useMemo(() => {
        const today = new Date();
        let minDate = new Date(today);
        let maxDate = new Date(today);
        minDate.setMonth(minDate.getMonth() - 1);
        maxDate.setMonth(maxDate.getMonth() + 3);
        for (const { issue } of visibleRows) {
            if (issue.startDate) {
                const d = new Date(issue.startDate);
                if (d < minDate)
                    minDate = d;
            }
            if (issue.endDate) {
                const d = new Date(issue.endDate);
                if (d > maxDate)
                    maxDate = d;
            }
            if (issue.dueDate) {
                const d = new Date(issue.dueDate);
                if (d > maxDate)
                    maxDate = d;
                if (d < minDate)
                    minDate = d;
            }
        }
        // Add padding
        minDate = addDays(minDate, -14);
        maxDate = addDays(maxDate, 14);
        return {
            startDate: minDate,
            endDate: maxDate,
            totalDays: daysBetween(minDate, maxDate),
        };
    }, [visibleRows]);
    const svgWidth = totalDays * dayWidth;
    const svgHeight = visibleRows.length * ROW_HEIGHT;
    const today = new Date();
    const todayOffset = daysBetween(startDate, today) * dayWidth;
    // Sync scroll between panes
    const handleRightScroll = useCallback(() => {
        if (leftPaneRef.current && rightPaneRef.current) {
            leftPaneRef.current.scrollTop = rightPaneRef.current.scrollTop;
        }
    }, []);
    const handleLeftScroll = useCallback(() => {
        if (leftPaneRef.current && rightPaneRef.current) {
            rightPaneRef.current.scrollTop = leftPaneRef.current.scrollTop;
        }
    }, []);
    // Resize handle
    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        resizingRef.current = true;
        const startX = e.clientX;
        const startWidth = leftPaneWidth;
        const handleMouseMove = (ev) => {
            if (!resizingRef.current)
                return;
            const newWidth = Math.max(200, Math.min(500, startWidth + ev.clientX - startX));
            setLeftPaneWidth(newWidth);
        };
        const handleMouseUp = () => {
            resizingRef.current = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [leftPaneWidth]);
    // Generate timeline header
    const renderHeader = () => {
        const headers = [];
        const gridLines = [];
        const current = new Date(startDate);
        if (zoom === 'month') {
            let lastMonth = -1;
            while (current <= endDate) {
                if (current.getMonth() !== lastMonth) {
                    lastMonth = current.getMonth();
                    const x = daysBetween(startDate, current) * dayWidth;
                    headers.push(_jsx("text", { x: x + 2, y: 14, className: "text-[10px] fill-text-secondary", children: formatMonth(current) }, `m-${current.getTime()}`));
                    gridLines.push(_jsx("line", { x1: x, y1: 20, x2: x, y2: svgHeight + 20, stroke: "#e5e7eb", strokeWidth: 1 }, `ml-${current.getTime()}`));
                }
                current.setDate(current.getDate() + 1);
            }
        }
        else if (zoom === 'week') {
            while (current <= endDate) {
                if (current.getDay() === 1) { // Monday
                    const x = daysBetween(startDate, current) * dayWidth;
                    headers.push(_jsx("text", { x: x + 2, y: 14, className: "text-[10px] fill-text-secondary", children: formatWeek(current) }, `w-${current.getTime()}`));
                    gridLines.push(_jsx("line", { x1: x, y1: 20, x2: x, y2: svgHeight + 20, stroke: "#e5e7eb", strokeWidth: 1 }, `wl-${current.getTime()}`));
                }
                current.setDate(current.getDate() + 1);
            }
        }
        else {
            while (current <= endDate) {
                const x = daysBetween(startDate, current) * dayWidth;
                if (current.getDay() !== 0 && current.getDay() !== 6) {
                    headers.push(_jsx("text", { x: x + dayWidth / 2, y: 14, textAnchor: "middle", className: "text-[9px] fill-text-secondary", children: current.getDate() }, `d-${current.getTime()}`));
                }
                if (current.getDay() === 1) {
                    gridLines.push(_jsx("line", { x1: x, y1: 20, x2: x, y2: svgHeight + 20, stroke: "#e5e7eb", strokeWidth: 1 }, `dl-${current.getTime()}`));
                }
                current.setDate(current.getDate() + 1);
            }
        }
        return { headers, gridLines };
    };
    const { headers, gridLines } = renderHeader();
    const renderBar = (issue, rowIndex) => {
        const defaultConfig = { color: '#3b82f6', height: 18 };
        const config = BAR_CONFIG[issue.issueType] ?? defaultConfig;
        const issueStart = issue.startDate || issue.dueDate;
        const issueEnd = issue.endDate || issue.dueDate;
        if (!issueStart && !issueEnd) {
            // No dates - show gray dot at today
            const cx = todayOffset;
            const cy = rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2 + 20;
            return (_jsx("circle", { cx: cx, cy: cy, r: 4, fill: "#d1d5db" }, issue.issueKey));
        }
        const barStart = issueStart ? daysBetween(startDate, new Date(issueStart)) * dayWidth : todayOffset;
        const barEnd = issueEnd ? daysBetween(startDate, new Date(issueEnd)) * dayWidth : barStart + dayWidth * 7;
        const barWidth = Math.max(barEnd - barStart, dayWidth);
        const y = rowIndex * ROW_HEIGHT + (ROW_HEIGHT - config.height) / 2 + 20;
        const isDashed = !issue.startDate || !issue.endDate;
        return (_jsxs("g", { children: [_jsx("rect", { x: barStart, y: y, width: barWidth, height: config.height, rx: 3, fill: config.color, opacity: 0.85, strokeDasharray: isDashed ? '4 2' : undefined, stroke: isDashed ? config.color : undefined, strokeWidth: isDashed ? 1 : 0 }), issue.progress > 0 && issue.children.length > 0 && (_jsx("rect", { x: barStart, y: y, width: barWidth * (issue.progress / 100), height: config.height, rx: 3, fill: config.color, opacity: 1 })), barWidth > 40 && (_jsx("text", { x: barStart + 4, y: y + config.height / 2 + 4, className: "text-[9px] fill-white font-medium", children: issue.issueKey }))] }, issue.issueKey));
    };
    const TYPE_ICONS_SMALL = {
        'Epic': { bg: '#ede9fe', letter: 'E' },
        'Story': { bg: '#dcfce7', letter: 'S' },
        'Task': { bg: '#dbeafe', letter: 'T' },
        'Bug': { bg: '#fee2e2', letter: 'B' },
        'Sub-task': { bg: '#e0f2fe', letter: 'ST' },
    };
    return (_jsxs("div", { className: "bg-white rounded-lg border border-border overflow-hidden", children: [_jsxs("div", { className: "flex items-center gap-2 px-3 py-2 border-b border-border bg-surface-muted", children: [_jsx("span", { className: "text-xs text-text-secondary mr-1", children: "Zoom:" }), ['day', 'week', 'month'].map(level => (_jsx("button", { onClick: () => setZoom(level), className: `px-2 py-0.5 text-xs rounded ${zoom === level
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-text-secondary hover:bg-gray-100'}`, children: ZOOM_CONFIG[level].label }, level)))] }), _jsxs("div", { className: "flex", style: { height: Math.max(svgHeight + 60, 200) }, children: [_jsxs("div", { ref: leftPaneRef, className: "overflow-y-auto border-r border-border flex-shrink-0", style: { width: leftPaneWidth }, onScroll: handleLeftScroll, children: [_jsx("div", { className: "h-5 border-b border-border bg-surface-muted sticky top-0 z-10 flex items-center px-2", children: _jsx("span", { className: "text-[10px] font-medium text-text-secondary", children: "Issue" }) }), visibleRows.map(({ issue, depth }) => {
                                const hasChildren = issue.children.length > 0;
                                const isExpanded = expandedKeys.has(issue.issueKey);
                                const defaultIcon = { bg: '#dbeafe', letter: 'T' };
                                const icon = TYPE_ICONS_SMALL[issue.issueType] ?? defaultIcon;
                                return (_jsxs("div", { className: "flex items-center gap-1 border-b border-border/30 hover:bg-surface-muted cursor-default", style: { height: ROW_HEIGHT, paddingLeft: `${depth * 16 + 4}px` }, children: [_jsx("button", { onClick: () => onToggleExpand(issue.issueKey), className: `w-3 h-3 flex items-center justify-center text-text-tertiary flex-shrink-0 ${hasChildren ? '' : 'invisible'}`, children: hasChildren && (_jsx("svg", { className: `w-2.5 h-2.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`, fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 3, d: "M9 5l7 7-7 7" }) })) }), _jsx("span", { className: "w-4 h-4 flex items-center justify-center rounded-sm text-[8px] font-bold flex-shrink-0", style: { backgroundColor: icon.bg }, children: icon.letter }), _jsx("span", { className: "text-[11px] font-mono text-text-secondary flex-shrink-0 w-14 truncate", children: issue.issueKey }), _jsx("span", { className: "text-[11px] text-text-primary truncate flex-1 min-w-0", children: issue.summary })] }, issue.issueKey));
                            })] }), _jsx("div", { className: "w-1 cursor-col-resize bg-border hover:bg-primary-300 flex-shrink-0", onMouseDown: handleResizeStart }), _jsx("div", { ref: rightPaneRef, className: "overflow-auto flex-1", onScroll: handleRightScroll, children: _jsxs("svg", { width: svgWidth, height: svgHeight + 20, className: "block", children: [_jsx("rect", { x: 0, y: 0, width: svgWidth, height: 20, fill: "#f9fafb" }), headers, gridLines, visibleRows.map((_, i) => (_jsx("rect", { x: 0, y: i * ROW_HEIGHT + 20, width: svgWidth, height: ROW_HEIGHT, fill: i % 2 === 0 ? '#ffffff' : '#fafafa' }, i))), _jsx("line", { x1: todayOffset, y1: 0, x2: todayOffset, y2: svgHeight + 20, stroke: "#ef4444", strokeWidth: 1.5, strokeDasharray: "4 3" }), visibleRows.map(({ issue }, i) => renderBar(issue, i))] }) })] })] }));
}
//# sourceMappingURL=PlanGanttView.js.map